// WEBSOCKETS

// To communicate with the backend, besides normal HTTP API calls we use
// *Websockets*. These stay open and allow two-way communication (the backend
// sends messages that the frontend didn't have to request first).

// We use the '@giantmachines/redux-websocket' library that allows us to send
// messages by dispatching Redux actions, and it turns received messages into
// Redux messages of its own that we could in theory handle only with reducers
// to update our state.

// However:

// - We have *multiple* Websockets. Right now there is the 'main' Websocket which
//   is opened immediately once there is a Simulation object, and the 'waterdepths'
//   Websocket that is only opened once a calculation core is actually running.

//   The library does this by allowing different *prefixes* in messages. However,
//   we intercept the incoming messages here and give them all the same prefix, so
//   that reducers can just handle them without having to know over which Websocket
//   a given message came. Connecting and sending does always need a
//   Websocket name, please use the helper function socketSend() from state/websockets.

// - Some incoming messages are *binary blobs* that can be turned into images or
//   arrays, by *asynchronous* functions. Reducers cannot be async, that is why these
//   are handled here and then turned into other Redux actions asynchronously.

// - There are also other *side effects*: the water depths Websocket can be opened
//   once the simulation state becomes 'running', once the water depths Websocket has
//   opened we need to send it our current bounds and zoom level so the backend
//   knows what water depth images to send, and probably more in the future. These
//   are also handled by this middleware.

// This middleware runs *after* the redux-websocket middleware. For some reason
// using middlewareApi.dispatch fails, and we often have to use the global appDispatch
// instead. This may or may not be a bug in some part of the system, but this workaround
// is good enough for now.

// See also:

// state/websockets where we keep the current state of the Websockets in Redux

// state/appState for various ways to start up the application; that imperative logic
// interacts with the logic here that reacts to incoming messages.

// Types
import Leaflet from "leaflet";
import { AnyAction, Dispatch, Middleware, MiddlewareAPI } from "redux";

import { getV3Api } from "../apiConfiguration";
import { appDispatch } from "../App";
import {
  ActiveSimulation,
  RECEIVE_ACTIVE_SIMULATIONS,
  REMOVE_INACTIVE_SIMULATION,
  UPDATE_ACTIVE_SIMULATION,
  UPDATE_ACTIVE_SIMULATION_PROGRESS,
} from "../state/activeSimulations";
import { backToStartScreen } from "../state/appState";
import { getHighLevelState } from "../state/appState";
import { receiveSourceRasterUrls } from "../state/layerRasterSourcesMenu";
import { sendMessage } from "../state/mainGui";
import { getMap, sendZoomAndBoundsToWebsocket } from "../state/map";
import {
  getElevationData,
  setInterpolatedGroundwater,
  setInterpolatedSurfacewater,
} from "../state/mapProfileLine";
import { setPumpActivities } from "../state/pumpActivity";
import {
  BreachChart,
  WaterFlowChart,
  addBreachChartData,
  addPumpDischargeChartData,
  addWaterflowchartData,
  addWaterlevelchartData,
} from "../state/selectedAsset";
import {
  addGroundwatterlevelchartData,
  addRainFalllevelchartData,
  addWaterlevelchartData as addWaterlevelchartDataPoint,
} from "../state/selectedPoint";
import {
  RECEIVED_WEBSOCKET_MESSAGE,
  WebsocketMessageAction,
  getSimulation,
  openWebsocket,
  receiveOneDeeData,
  receiveTwoDeeData,
} from "../state/simulation";
import { receiveWaterDepthImage } from "../state/waterdepth";
import {
  Websocket,
  WebsocketStatus,
  getWebsockets,
  isWebsocketPrefix,
  setWebsocketStatus,
  socketConnect,
  socketSend,
} from "../state/websockets";
import { forceValidOrNull, isValidData } from "../utils/isValidData";

// These methods except on Blob but Typescript doesn't know about them
interface ABlob extends Blob {
  arrayBuffer: () => Promise<ArrayBuffer>;
  slice: ((n: number) => ABlob) & ((n: number, m: number) => ABlob);
  text: () => Promise<string>;
}

// should mainWebsocketInterval be actually stored in the redux state ?!
let mainWebsocketFrame: null | number = null;
const setMainWebsocketHeartbeat = (api: MiddlewareAPI) => {
  let lastTimestamp = 0;
  const sendHeartbeat = (timestamp: number) => {
    const state = api.getState();
    const mainWebsocket = getWebsockets(state)[Websocket.Main];
    if (mainWebsocket.status === WebsocketStatus.Open) {
      if (!lastTimestamp || timestamp - lastTimestamp >= 5000) {
        lastTimestamp = timestamp;
        appDispatch(socketSend({ event_name: "heartbeat" }, Websocket.Main));
      }
      mainWebsocketFrame = window.requestAnimationFrame(sendHeartbeat);
    } else {
      // main websocket is closed
      const simulation = getSimulation(state);
      if (simulation && simulation.id) {
        //console.log('open MAIN websocket again because it was closed');
        appDispatch(openWebsocket());
      }
    }
  };
  mainWebsocketFrame = window.requestAnimationFrame(sendHeartbeat);
};
const clearMainWebsocketHeartBeat = () => {
  if (mainWebsocketFrame !== null) window.cancelAnimationFrame(mainWebsocketFrame);
  mainWebsocketFrame = null;
};

const WebsocketMiddleware: Middleware =
  (api: MiddlewareAPI) =>
  (next: Dispatch) =>
  <A extends AnyAction>(action: A): A | undefined => {
    const actionType = action.type;

    const [prefix, messageType] = actionType.split("::");

    if (!isWebsocketPrefix(prefix)) {
      return next(action);
    }

    // console.log("MESSAGE", action);

    // We now know the message has one of the Websocket prefixes, the rest depends on
    // what kind of message it is. It can be either sent by us to the Websocket (let
    // it continue unchanged) or received by us from the Websocket, in which case we
    // handle it.
    switch (messageType) {
      case "CONNECT":
      case "SEND":
        // These are *sent* messages by us, send them on to other middleware.
        return next(action);
      case "MESSAGE": {
        // These are the main things we are interested in. Blobs are handled separately,
        // other messages are turned into other actions without the prefix so that
        // reducers don't have to care from which Websocket a message came.
        const message = (action as unknown as WebsocketMessageAction).payload.message;

        if (message instanceof Blob) {
          if (
            prefix === Websocket.WaterlevelChart ||
            prefix === Websocket.WaterlevelChartPoint ||
            prefix === Websocket.GroundWaterChartPoint ||
            prefix === Websocket.RainGraphOnPointChart
          ) {
            handleWaterChartBlobMessage(message as ABlob, prefix);
            return;
          } else if (prefix === Websocket.WaterlevelProfileChart) {
            handleWaterChartProfileBlobMessage(message as ABlob);
            return;
          } else if (prefix === Websocket.WaterFlowChart) {
            handleWaterFlowChartProfileBlobMessage(message as ABlob);
            return;
          } else if (prefix === Websocket.PumpDischargeChart) {
            handlePumpDischargeBlobMessage(message as ABlob);
            return;
          } else if (prefix === Websocket.BreachChart) {
            handleBreachChartBlobMessage(message as ABlob);
            return;
          } else {
            handleBlobMessage(message as ABlob);
            return;
          }
        } else if (prefix === Websocket.ActiveSimulations) {
          const parsedResult = JSON.parse(message);

          if (parsedResult.type === "active-simulations") {
            // Initialize with an array
            const arrayOfActiveSimulations: string[] = Object.values(parsedResult.data);
            const activeSimulations = arrayOfActiveSimulations.map((simulation) =>
              JSON.parse(simulation)
            );
            appDispatch({
              type: RECEIVE_ACTIVE_SIMULATIONS,
              activeSimulations: activeSimulations,
            });
          } else if (parsedResult.type === "active-simulation") {
            // A single simulation.
            // Data is an object with the uid as single key, and the active simulation
            // as a JSON encoded string as value.
            if (!parsedResult.data) {
              return;
            }
            const uid = Object.keys(parsedResult.data)[0];
            if (!uid) {
              return;
            }

            const simulationData = parsedResult.data[uid];
            const simulation =
              typeof simulationData === "string"
                ? (JSON.parse(simulationData) as ActiveSimulation)
                : simulationData;

            appDispatch({
              type: UPDATE_ACTIVE_SIMULATION,
              activeSimulation: simulation,
            });
          } else if (parsedResult.type === "progress") {
            appDispatch({
              type: UPDATE_ACTIVE_SIMULATION_PROGRESS,
              uid: parsedResult.data.simulation_id,
              progress: parsedResult.data.progress,
            });
          } else if (parsedResult.type === "status") {
            const { status } = parsedResult.data;
            if (status === "crashed" || status === "finished" || status === "ended") {
              appDispatch({
                type: REMOVE_INACTIVE_SIMULATION,
                uid: parsedResult.data.simulation_id,
              });
            }
          }
          return;
        } else {
          const parsed = JSON.parse(message);
          if (parsed.type === "visualization" && parsed.data.layer === "waterdepth") {
            openWaterdepthWebsocket(api, next);
            // todo: pass the websocket url instead of making the function do an extra api call to retrieve websocket url
            // openWaterdepthWebsocket(parsed.data.websocket.url, next);
          }
          ifRasterEditUpdateProfileData(parsed);

          // do something when simulation is crashed
          ifSimulationCrashedDisplayIt(parsed);

          if (parsed.error) {
            if (parsed.error.includes("websocket connection limit")) {
              // when connection limit reached (5), we first display an
              // error message on snackbar and then after 5 seconds,
              // redirect user back to the start screen
              appDispatch(sendMessage("error.connection_limit_reached", 10000, "error"));
              setTimeout(() => {
                appDispatch(backToStartScreen());
              }, 10000);
            } else if (parsed.error.includes("Simulation is not active")) {
              const state = api.getState();
              const hightLevelState = getHighLevelState(state);
              if (hightLevelState !== "DOWNLOAD") {
                // when simulation is no longer active we display an error message and after this
                // redirect user back to the start screen
                console.error("Simulation is not active", parsed.error);
                appDispatch(sendMessage("error.simulation_no_longer_active", 10000, "error"));
                setTimeout(() => {
                  appDispatch(backToStartScreen());
                }, 10000);
              }
            } else {
              console.error("Caught unknown websocket error message:", parsed.error);
            }
          }

          if (parsed.type && parsed.data && parsed.type === "visualization" && parsed.data.layer) {
            appDispatch(receiveSourceRasterUrls(parsed));
            return;
          } else {
            const action = {
              type: RECEIVED_WEBSOCKET_MESSAGE,
              payload: parsed,
            } as unknown as A; // Typescript doesn't want to believe that that is an action
            return next(action);
          }
        }
      }
      case "OPEN": {
        const result = next(setWebsocketStatus(prefix, WebsocketStatus.Open) as unknown as A);

        if (prefix === Websocket.Waterdepths) {
          // Also send bounds to the opened socket for 2D images
          const state = api.getState();
          const map = getMap(state);
          if (map) {
            const socketSend = sendZoomAndBoundsToWebsocket(map);
            if (socketSend) {
              appDispatch(socketSend);
            }
          }
        }
        if (prefix === Websocket.Main) {
          clearMainWebsocketHeartBeat();
          setMainWebsocketHeartbeat(api);
          window.onblur = () => clearMainWebsocketHeartBeat();
          window.onfocus = () => {
            clearMainWebsocketHeartBeat();
            setMainWebsocketHeartbeat(api);
          };
        }

        return result;
      }
      case "CLOSED":
        if (prefix === Websocket.Main) {
          clearMainWebsocketHeartBeat();
        }
        return next(setWebsocketStatus(prefix, WebsocketStatus.Closed) as unknown as A);
      case "DISCONNECT":
        return next(setWebsocketStatus(prefix, WebsocketStatus.Closed) as unknown as A);
      case "ERROR":
        // Log and assume it is closed
        console.error("Caught websocket error message:", action);
        if (prefix === Websocket.Main) {
          clearMainWebsocketHeartBeat();
        }
        return next(setWebsocketStatus(prefix, WebsocketStatus.Closed) as unknown as A);
      default:
        console.error("Unknown websocket action", action);
        return;
    }
  };

export default WebsocketMiddleware;

const ifRasterEditUpdateProfileData = (parsed: unknown) => {
  // Check for raster Edit
  const data = (parsed as { data?: { model?: string } }).data;
  const model: string = (data && data.model) || "";
  if (model === "RasterEdit") {
    appDispatch(getElevationData());
  }
};

const ifSimulationCrashedDisplayIt = (parsed: {
  type?: string;
  data?: { data?: { name: string } };
}) => {
  if (
    parsed.type === "status" &&
    parsed.data &&
    parsed.data.data &&
    parsed.data.data.name === "crashed"
  ) {
    appDispatch(sendMessage("error.simulation_crashed", 10000, "error"));
  }
};

const handleWaterChartBlobMessage = (message: ABlob, websocketType: Websocket) => {
  message.arrayBuffer().then((headerBuffer) => {
    const messageFloat32Array = new Float32Array(headerBuffer);
    const messageArray = Array.from(messageFloat32Array);
    const waterLevelChart = messageArray.reduce((accumulator, currentValue) => {
      const accumulatorCopy = [...accumulator];
      // if the array is empty -> then add a new item
      // or if the last item of the array already has a value -> then add a new item
      if (
        accumulatorCopy.length === 0 ||
        // @ts-ignore
        accumulatorCopy[accumulatorCopy.length - 1].value !== null
      ) {
        // @ts-ignore
        accumulatorCopy.push({ timestamp: currentValue, value: null }); // null means data is not yet copied
        return accumulatorCopy;
      }
      // else add the value to the last item
      else {
        // @ts-ignore
        accumulatorCopy[accumulatorCopy.length - 1].value = isValidData(currentValue)
          ? currentValue
          : undefined; // undefined means no measuring data is available
        return accumulatorCopy;
      }
    }, []);
    if (websocketType === Websocket.WaterlevelChart) {
      appDispatch(addWaterlevelchartData(waterLevelChart));
    } else if (websocketType === Websocket.WaterlevelChartPoint) {
      appDispatch(addWaterlevelchartDataPoint(waterLevelChart));
    } else if (websocketType === Websocket.GroundWaterChartPoint) {
      appDispatch(addGroundwatterlevelchartData(waterLevelChart));
    } else if (websocketType === Websocket.RainGraphOnPointChart) {
      appDispatch(addRainFalllevelchartData(waterLevelChart));
    }
  });
};

const handlePumpDischargeBlobMessage = (message: ABlob) => {
  message.arrayBuffer().then((headerBuffer) => {
    const messageFloat32Array = new Float32Array(headerBuffer);
    const messageArray = Array.from(messageFloat32Array);
    const pumpDischargeChart = messageArray.reduce((accumulator, currentValue) => {
      const accumulatorCopy = [...accumulator];
      // if the array is empty -> then add a new item
      // or if the last item of the array already has a value -> then add a new item
      if (
        accumulatorCopy.length === 0 ||
        // @ts-ignore
        accumulatorCopy[accumulatorCopy.length - 1].value !== null
      ) {
        // @ts-ignore
        accumulatorCopy.push({ timestamp: currentValue, value: null }); // null means data is not yet copied
        return accumulatorCopy;
      }
      // else add the value to the last item
      else {
        // @ts-ignore
        accumulatorCopy[accumulatorCopy.length - 1].value = isValidData(currentValue)
          ? currentValue
          : undefined; // undefined means no measuring data is available
        return accumulatorCopy;
      }
    }, []);
    appDispatch(addPumpDischargeChartData(pumpDischargeChart));
  });
};

const handleBreachChartBlobMessage = (message: ABlob) => {
  message.arrayBuffer().then((headerBuffer) => {
    const messageFloat32Array = new Float32Array(headerBuffer);
    const messageArray = Array.from(messageFloat32Array);

    const breachData: BreachChart = [];
    let tempBreachData = {
      timestamp: 0,
      discharge: 0, // m³/s
      width: 0, // m
      depth: 0, // m
      area: 0, // m²
    };

    messageArray.forEach((value, indx) => {
      const modu = indx % 5;
      if (modu === 0) {
        tempBreachData = {
          timestamp: 0,
          discharge: 0, // m³/s
          width: 0, // m
          depth: 0, // m
          area: 0, // m²
        };
        tempBreachData.timestamp = value;
      } else if (modu === 1) {
        tempBreachData.width = value;
      } else if (modu === 2) {
        tempBreachData.depth = value;
      } else if (modu === 3) {
        tempBreachData.discharge = value;
      } else if (modu === 4) {
        tempBreachData.area = value;
        breachData.push(tempBreachData);
      }
    });

    appDispatch(addBreachChartData(breachData));
  });
};

const handleWaterFlowChartProfileBlobMessage = (message: ABlob) => {
  interface TempWaterFlowChartItem {
    timestamp: number | null;
    velocity: number | null;
    discharge: number | null;
  }

  message.arrayBuffer().then((headerBuffer) => {
    const messageFloat32Array = new Float32Array(headerBuffer);
    const messageArray = Array.from(messageFloat32Array);

    const waterFlowChart: WaterFlowChart = [];
    let tempItem: TempWaterFlowChartItem = {
      timestamp: null,
      velocity: null,
      discharge: null,
    };

    messageArray.forEach((currentValue: number) => {
      if (tempItem.timestamp === null) {
        tempItem = { ...tempItem, timestamp: currentValue };
      } else if (tempItem.velocity === null) {
        tempItem = { ...tempItem, velocity: currentValue };
      } else {
        tempItem = { ...tempItem, discharge: currentValue };
        if (
          tempItem.timestamp !== null &&
          tempItem.velocity !== null &&
          tempItem.discharge !== null
        ) {
          // below line gives typescript error so I replace with line blow that.
          // waterFlowChart.push(tempItem);
          waterFlowChart.push({
            timestamp: tempItem.timestamp,
            velocity: tempItem.velocity,
            discharge: tempItem.discharge,
          });
        }
        tempItem = { timestamp: null, velocity: null, discharge: null };
      }
    });
    appDispatch(addWaterflowchartData(waterFlowChart));
  });
};

const handleWaterChartProfileBlobMessage = (message: ABlob) => {
  message.arrayBuffer().then((headerBuffer) => {
    const messageFloat32Array = new Float32Array(headerBuffer);
    const messageArray = Array.from(messageFloat32Array);
    // Do we need timestamp ?
    // const timestamp = messageArray[0];
    const values = messageArray.slice(1); // remove timestamp from values first
    if (values.length % 2 === 0) {
      // an even length indicates 2 odd arrays combined, first halve is surface water second half groundwater
      const surfaceWater = forceValidOrNull(values.slice(0, values.length / 2));
      const groundWater = forceValidOrNull(values.slice(values.length / 2));
      appDispatch(setInterpolatedSurfacewater(surfaceWater));
      appDispatch(setInterpolatedGroundwater(groundWater));
    } else {
      // and odd length array means only the surface water array is there
      appDispatch(setInterpolatedSurfacewater(values));
    }
  });
};

const handleBlobMessage = (message: ABlob) => {
  // The first 2 bytes say what kind of message this is.
  const type = message.slice(0, 2);
  const rest = message.slice(2);

  type.text().then((t) => {
    if (t === "2d") handle2DBlob(rest);
    if (t === "2f") handle2DFlowBlob(rest);
    if (t === "1d") handle1DBlob(rest);
    if (t === "pu") handlePumpsBlob(rest);
  });
};

const handle2DBlob = (message: ABlob) => {
  // The blob is a 40-byte header containing timing and bounds
  // information, plus an image.

  const header = message.slice(0, 40);
  const image = message.slice(40);

  header.arrayBuffer().then((headerBuffer) => {
    const headerArray = new Float64Array(headerBuffer);

    const timing = headerArray[0];
    const sw = Leaflet.Projection.SphericalMercator.unproject(
      Leaflet.point(headerArray[1], headerArray[2])
    );
    const ne = Leaflet.Projection.SphericalMercator.unproject(
      Leaflet.point(headerArray[3], headerArray[4])
    );

    const data_bounds = Leaflet.latLngBounds(sw, ne);

    const objectUrl = window.URL.createObjectURL(image);
    const img = document.createElement("img");
    img.onload = (_) => {
      appDispatch(receiveWaterDepthImage(objectUrl, data_bounds, timing));
    };
    img.src = objectUrl;
  });
};

const handle2DFlowBlob = (message: ABlob) => {
  const HEADER_SIZE = 8;
  const header = message.slice(0, HEADER_SIZE);
  const array = message.slice(HEADER_SIZE);

  header.arrayBuffer().then((headerData) => {
    const headerFloat64 = new Float64Array(headerData);
    const timestamp_1d = headerFloat64[0];

    array.arrayBuffer().then((arrayBuffer) => {
      const twoDeeData = new Float32Array(arrayBuffer);

      // twoDeeData consists of *two* equal-length arrays
      const halfLength = twoDeeData.length / 2;
      const ucx = twoDeeData.slice(0, halfLength);
      const ucy = twoDeeData.slice(halfLength);

      appDispatch(receiveTwoDeeData(ucx, ucy));
      appDispatch(socketSend({ processed_1d: timestamp_1d }, Websocket.Waterdepths));
    });
  });
};

const handle1DBlob = (message: ABlob) => {
  // The blob is a 8-byte header containing timing, plus an array. We
  // ignore the timing for now
  // (as we already use the image's timing to update the backend).

  const HEADER_SIZE = 2 * 8; // 2 Float64 numbers
  const header = message.slice(0, HEADER_SIZE);
  const array = message.slice(HEADER_SIZE);

  header.arrayBuffer().then((headerData) => {
    const headerFloat64 = new Float64Array(headerData);
    const timestamp_1d = headerFloat64[0];
    const lineIndexOffset = Math.floor(headerFloat64[1]);

    array.arrayBuffer().then((arrayBuffer) => {
      const oneDeeData = new Float32Array(arrayBuffer);

      // oneDeeData consists of *two* equal-length arrays
      const halfLength = oneDeeData.length / 2;
      const u1 = oneDeeData.slice(0, halfLength);
      const qw = oneDeeData.slice(halfLength);

      appDispatch(receiveOneDeeData(lineIndexOffset, u1, qw));
      appDispatch(socketSend({ processed_1d: timestamp_1d }, Websocket.Waterdepths));
    });
  });
};

const handlePumpsBlob = (message: ABlob) => {
  const HEADER_SIZE = 8;
  const header = message.slice(0, HEADER_SIZE);
  const array = message.slice(HEADER_SIZE);

  header.arrayBuffer().then((headerData) => {
    const headerFloat64 = new Float64Array(headerData);
    const timestamp_1d = headerFloat64[0];

    array.arrayBuffer().then((arrayBuffer) => {
      const pumpData = new Float32Array(arrayBuffer);
      appDispatch(setPumpActivities(Array.from(pumpData)));
      appDispatch(socketSend({ processed_pump: timestamp_1d }, Websocket.Waterdepths));
    });
  });
};

const openWaterdepthWebsocket = (middlewareApi: MiddlewareAPI, next: Dispatch) => {
  // todo: pass the websocket url instead of making the function do an extra api call to retrieve websocket url
  // const openWaterdepthWebsocket = (websocketUrl: string, next: Dispatch) => {
  // appDispatch(socketConnect(websocketUrl, Websocket.Waterdepths));
  const simulation = getSimulation(middlewareApi.getState());
  if (simulation === null) {
    return;
  }
  const waterdepthSimulationId = simulation.id;
  if (waterdepthSimulationId === undefined) return;

  next(setWebsocketStatus(Websocket.Waterdepths, WebsocketStatus.Opening));

  // Call the API to hear what the required Websocket URL is
  const v3Api = getV3Api();

  setTimeout(async function () {
    try {
      const websocketResponse = await v3Api.simulationsVisualisationsWaterdepthList({
        simulationPk: waterdepthSimulationId,
      });

      const waterdepth = websocketResponse;

      // Connect to the URL
      // I would think that using middlewareApi.dispatch would go through the
      // whole stack of middlewares again, but that doesn't actually open the
      // socket or seem to reach that middleware at all. So that's why I use
      // the imported store.dispatch function directly. --RG20200207
      appDispatch(
        socketConnect(waterdepth.websocketUrl + "&flow_2d_enabled=true", Websocket.Waterdepths)
      );
    } catch (error: unknown) {
      console.error("Error opening waterdepth:", error);
    }
  }, 5000);
};
